import React from 'react';
import { Grid, Typography } from '@mui/material';
import { Form } from 'formik';
import * as Yup from 'yup';
import { isSquaredDisplay } from 'components/content/models/ContentModule';
import { MuiForm } from 'lib/Form';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { ImageFormField } from 'lib/form/fields/ImageFormField';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';
import { getImageAspect, getImagePickerDescriptionByDisplay, MarketingMessageProps } from './base';
import { Row } from 'ui/Flex';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { isEmptyString, isString } from 'lib/typeguards';

interface IContent {
    imageUrl: string;
    pageTitle: string;
    title: string;
    subtitle: string;
    body: string;
    buttonTitle: string;
    buttonUrl: string;
}

interface INativeItemProperties {
    imageUrl: string;
    title?: string;
    subtitle?: string;
    subtitleTwo?: string;
    invertTextColour?: boolean;
    showTextBackgroundBar?: boolean;
    content: IContent;
    name?: string;
}

interface INativeFormValues {
    name: string;
    imageUrl: string;
    title: string;
    subtitle: string;
    subtitleTwo: string;
    invertTextColour: boolean;
    showTextBackgroundBar: boolean;
    contentBody: string;
    contentButtonTitle: string;
    contentButtonUrl: string;
    contentImageUrl: string;
    contentPageTitle: string;
    contentSubtitle: string;
    contentTitle: string;
}

function getNonEmptyStringOrUndefined(value?: string): string | undefined {
    if (!isString(value) || isEmptyString(value)) {
        return undefined;
    }
    return value;
}

function getResourceDataFromFormData(data: INativeFormValues): INativeItemProperties {
    return {
        content: {
            body: getNonEmptyStringOrUndefined(data.contentBody),
            buttonTitle: getNonEmptyStringOrUndefined(data.contentButtonTitle),
            buttonUrl: getNonEmptyStringOrUndefined(data.contentButtonUrl),
            imageUrl: getNonEmptyStringOrUndefined(data.contentImageUrl),
            pageTitle: getNonEmptyStringOrUndefined(data.contentPageTitle),
            subtitle: getNonEmptyStringOrUndefined(data.contentSubtitle),
            title: getNonEmptyStringOrUndefined(data.contentTitle)
        },
        name: data.name,
        imageUrl: data.imageUrl,
        title: data.title || '',
        subtitle: data.subtitle || '',
        subtitleTwo: data.subtitleTwo || '',
        invertTextColour: data.invertTextColour || false,
        showTextBackgroundBar: data.showTextBackgroundBar || false
    };
}

export const NativeMessageProperties: React.FC<MarketingMessageProps<INativeItemProperties>> = props => {
    const { display, properties = {} as INativeItemProperties, onSubmit, name, contextForm } = props;
    const hideMarketingMessageOverlay = isSquaredDisplay(display);

    const initialValues = React.useMemo(
        () => ({
            name: name || '',
            imageUrl: properties.imageUrl || '',
            title: properties.title || '',
            subtitle: properties.subtitle || '',
            subtitleTwo: properties.subtitleTwo || '',
            invertTextColour: properties.invertTextColour || false,
            showTextBackgroundBar: properties.showTextBackgroundBar || false,
            contentBody: properties.content?.body || '',
            contentButtonTitle: properties.content?.buttonTitle || '',
            contentButtonUrl: properties.content?.buttonUrl || '',
            contentImageUrl: properties.content?.imageUrl || '',
            contentPageTitle: properties.content?.pageTitle || '',
            contentSubtitle: properties.content?.subtitle || '',
            contentTitle: properties.content?.title || ''
        }),
        [name, properties]
    );

    const validationSchema = React.useMemo(
        () =>
            Yup.object().shape({
                imageUrl: Yup.string().required('This field is required.').url('Invalid URL.'),
                name: Yup.string().required('This field is required.'),
                contentPageTitle: Yup.string().required('This field is required.'),
                contentTitle: Yup.string().required('This field is required.'),
                contentBody: Yup.string().required('This field is required.'),
                contentButtonUrl: Yup.string().notRequired().url('Invalid URL.')
            }),
        []
    );

    const handleSubmit = React.useCallback(
        (data: INativeFormValues) => {
            onSubmit(getResourceDataFromFormData(data));
        },
        [onSubmit]
    );

    return (
        <MuiForm onSubmit={handleSubmit} initialValues={initialValues} validationSchema={validationSchema}>
            {({ submitForm, isSubmitting, isValid }) => (
                <Form>
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <ImageFormField
                                name="imageUrl"
                                label={<MuiFormLabel required>Image</MuiFormLabel>}
                                aspect={getImageAspect(display)}
                                accept={['image/jpeg', 'image/png', 'image/gif']}
                                description={getImagePickerDescriptionByDisplay(display)}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextFormField
                                name="name"
                                label={<MuiFormLabel required>Reporting Name</MuiFormLabel>}
                                description="This code is used to track the number of marketing module item taps on anchor screen"
                            />
                        </Grid>
                        {!hideMarketingMessageOverlay && (
                            <>
                                <Grid size={12}>
                                    <Grid container direction="column">
                                        <Typography color="primary">Marketing message overlay</Typography>
                                    </Grid>
                                </Grid>
                                <Grid size={12}>
                                    <TextFormField name="title" label="Title" />
                                </Grid>
                                <Grid size={12}>
                                    <TextFormField name="subtitle" label="Subtitle" />
                                </Grid>
                                <Grid size={12}>
                                    <TextFormField name="subtitleTwo" label="Subtitle two" />
                                </Grid>
                                <Grid size={12}>
                                    <SwitchFormField name="invertTextColour" label="Invert text colour" />
                                </Grid>
                                <Grid size={12}>
                                    <SwitchFormField
                                        name="showTextBackgroundBar"
                                        label="Show text background bar"
                                    />
                                </Grid>
                            </>
                        )}
                        <Grid size={12}>
                            <Grid container direction="column">
                                <Typography color="primary">Content</Typography>
                                <Typography variant="caption">
                                    Configure what you want your users to see when they tap on your marketing
                                    item
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid size={12}>
                            <ImageFormField
                                name="contentImageUrl"
                                label="Content image"
                                aspect={getImageAspect()}
                                accept={['image/jpeg', 'image/png', 'image/gif']}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextFormField
                                name="contentPageTitle"
                                label={<MuiFormLabel required>Content page title</MuiFormLabel>}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextFormField
                                name="contentTitle"
                                label={<MuiFormLabel required>Content title</MuiFormLabel>}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextFormField name="contentSubtitle" label="Content subtitle" />
                        </Grid>
                        <Grid size={12}>
                            <TextFormField
                                name="contentBody"
                                label={<MuiFormLabel required>Content body</MuiFormLabel>}
                                multiline
                                rows={4}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextFormField name="contentButtonTitle" label="Button title" />
                        </Grid>
                        <Grid size={12}>
                            <TextFormField name="contentButtonUrl" label="Button url" />
                        </Grid>
                        {contextForm()}
                        <Grid size={12}>
                            <Row gutter align="flex-end" valign="center">
                                <LoadingButton
                                    color="primary"
                                    variant="contained"
                                    disabled={isSubmitting || !isValid}
                                    loading={isSubmitting}
                                    onClick={submitForm}
                                >
                                    Submit
                                </LoadingButton>
                            </Row>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </MuiForm>
    );
};
